import React, { useEffect, useRef, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { AtSign, Loader2, Check, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { Eyebrow } from '../ui/Eyebrow';
import { validateUsername } from '../../lib/usernameModeration';

interface Props {
  user: User;
  onComplete: () => void;
}

type CheckState =
  | { kind: 'idle' }
  | { kind: 'too-short' }
  | { kind: 'reserved' }
  | { kind: 'banned' }
  | { kind: 'invalid' }
  | { kind: 'checking' }
  | { kind: 'available' }
  | { kind: 'taken' }
  | { kind: 'error'; message: string };

const ALLOWED_CHAR_RE = /[a-z0-9_]/;

function sanitize(raw: string): string {
  const lower = raw.toLowerCase();
  let out = '';
  for (const ch of lower) {
    if (ALLOWED_CHAR_RE.test(ch)) out += ch;
    if (out.length >= 20) break;
  }
  return out;
}

export function UsernameModal({ user, onComplete }: Props) {
  useBodyScrollLock(true);
  const [value, setValue] = useState('');
  const [check, setCheck] = useState<CheckState>({ kind: 'idle' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length === 0) {
      setCheck({ kind: 'idle' });
      return;
    }
    const validation = validateUsername(value);
    if (!validation.ok) {
      switch (validation.reason) {
        case 'too_short':
          setCheck({ kind: 'too-short' });
          return;
        case 'reserved_name':
          setCheck({ kind: 'reserved' });
          return;
        case 'banned_word':
          setCheck({ kind: 'banned' });
          return;
        default:
          setCheck({ kind: 'invalid' });
          return;
      }
    }
    setCheck({ kind: 'checking' });
    debounceRef.current = setTimeout(async () => {
      try {
        const { data, error: qErr } = await supabase
          .from('profiles_public')
          .select('user_id')
          .eq('username', value)
          .maybeSingle();
        if (qErr) {
          setCheck({ kind: 'error', message: 'Não foi possível verificar.' });
          return;
        }
        if (data && data.user_id !== user.id) {
          setCheck({ kind: 'taken' });
        } else {
          setCheck({ kind: 'available' });
        }
      } catch {
        setCheck({ kind: 'error', message: 'Não foi possível verificar.' });
      }
    }, 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, user.id]);

  const canSubmit = check.kind === 'available' && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const { error: upErr } = await supabase
      .from('profiles')
      .update({ username: value })
      .eq('user_id', user.id);
    setSubmitting(false);
    if (upErr) {
      // Em caso de race condition (UNIQUE constraint), trata como já em uso.
      if (upErr.message?.toLowerCase().includes('unique') || upErr.code === '23505') {
        setCheck({ kind: 'taken' });
        setError('Esse @username acabou de ser usado. Escolha outro.');
        return;
      }
      setError('Não foi possível salvar. Tente novamente.');
      return;
    }
    onComplete();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Escolha seu username"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60"
    >
      <div className="relative w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-lg px-5 py-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-1.5">
          <AtSign size={18} strokeWidth={2.25} className="text-green-700" />
          <Eyebrow>Bem-vindo ao novo BoraTrocar</Eyebrow>
        </div>
        <h2 className="font-display font-bold text-h2 text-ink-900 mb-1.5 tracking-[-0.01em]">
          Escolha seu @username
        </h2>
        <p className="text-small text-ink-500 leading-snug mb-4">
          Com ele, seus amigos podem te encontrar no app. Pode trocar depois nas configurações.
        </p>

        <label className="block">
          <span className="sr-only">Username</span>
          <div
            className={[
              'h-11 px-3 flex items-center gap-1 rounded-md border-[1.5px] bg-white',
              check.kind === 'taken' ||
              check.kind === 'error' ||
              check.kind === 'reserved' ||
              check.kind === 'banned' ||
              check.kind === 'invalid'
                ? 'border-ruby-500'
                : check.kind === 'available'
                  ? 'border-green-700'
                  : 'border-ink-300 focus-within:border-green-700',
            ].join(' ')}
          >
            <span className="font-display font-bold text-ink-500 text-body select-none">@</span>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(sanitize(e.target.value))}
              placeholder="seunome"
              autoFocus
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={20}
              className="flex-1 bg-transparent text-body font-body text-ink-900 outline-none"
              aria-label="Username"
              aria-invalid={
                check.kind === 'taken' ||
                check.kind === 'error' ||
                check.kind === 'reserved' ||
                check.kind === 'banned' ||
                check.kind === 'invalid'
              }
            />
            {check.kind === 'checking' && (
              <Loader2 size={14} className="animate-spin text-green-700 shrink-0" />
            )}
            {check.kind === 'available' && (
              <Check size={16} strokeWidth={2.5} className="text-green-700 shrink-0" />
            )}
          </div>
        </label>

        <div className="min-h-[20px] mt-2">
          {check.kind === 'too-short' && (
            <p className="text-small text-ink-500">Mínimo 4 caracteres.</p>
          )}
          {check.kind === 'invalid' && (
            <p className="text-small text-ruby-700">Use apenas letras minúsculas, números e _.</p>
          )}
          {check.kind === 'reserved' && (
            <p className="text-small text-ruby-700">
              Esse nome é muito comum. Adicione um sobrenome ou número (ex.: andresilva, andre_g).
            </p>
          )}
          {check.kind === 'banned' && (
            <p className="text-small text-ruby-700">
              Esse nome de usuário não é permitido. Escolha outro.
            </p>
          )}
          {check.kind === 'checking' && (
            <p className="text-small text-ink-500 inline-flex items-center gap-1.5">
              <Loader2 size={12} className="animate-spin" /> Verificando...
            </p>
          )}
          {check.kind === 'available' && (
            <p className="text-small text-green-700 font-display font-semibold inline-flex items-center gap-1.5">
              <Check size={14} strokeWidth={2.5} /> @{value} está disponível
            </p>
          )}
          {check.kind === 'taken' && (
            <p className="text-small text-ruby-700">Esse @username já está em uso.</p>
          )}
          {check.kind === 'error' && (
            <p className="text-small text-ruby-700 inline-flex items-center gap-1.5">
              <AlertTriangle size={12} strokeWidth={2.25} /> {check.message}
            </p>
          )}
        </div>

        {error && (
          <p className="text-small text-ruby-700 bg-ruby-100 border border-ruby-500 rounded-md px-3 py-2 mt-3">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={[
            'mt-5 w-full h-11 rounded-md font-display font-bold inline-flex items-center justify-center gap-2 btn-focus',
            canSubmit
              ? 'bg-green-700 text-white hover:bg-green-900'
              : 'bg-ink-100 text-ink-500 cursor-not-allowed',
          ].join(' ')}
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Salvando...
            </>
          ) : (
            'Confirmar'
          )}
        </button>

        <p className="text-[11px] text-ink-500 mt-4 leading-snug">
          Use letras minúsculas, números e underscore (_). De 4 a 20 caracteres.
        </p>
      </div>
    </div>
  );
}
